import React from 'react';
import env from '../env.json';
import * as locales from "date-fns/locale";

export const locale: { lang: string, resource: Record<string, string>, __: (src?: string | LocaleString | null) => string } = {
    lang: env.DEFAULT_LANGUAGE,
    resource: {},
    __: (src?: string | LocaleString | null) => {
        return src ? (typeof src === 'string' ?
            (locale.resource[src] || src) :
            (src[locale.lang] || src['default'])) : '';
    }
};

function detectLanguage(lang: string | readonly string[]): [string, Record<string, string>] {
    let result: [string, any] = [env.DEFAULT_LANGUAGE, {}];
    if (typeof lang === 'string') {
        lang = [lang];
    }
    for (const l of lang) {
        try {
            result[1] = require('../i18n/' + l + '.json');
            result[0] = l;
            break;
        } catch {
            result[1] = {};
        }
    }
    return result;
}

export type LocaleString = {
    [lang: string]: string
};

const LocaleContext = React.createContext<[(src?: string | LocaleString | null) => string, string, (lang: string) => void, date: locales.Locale]>([
    () => '', env.DEFAULT_LANGUAGE, () => { }, locales.enUS
]);

export function LocaleProvider(props: { children: JSX.Element, init?: string }) {
    const {0: lang, 1: setLang} = React.useState<string>(env.DEFAULT_LANGUAGE);
    const {0: resource, 1: setResource} = React.useState<Record<string, string>>({});
    let __ = React.useCallback((src?: string | LocaleString | null) => {
        return src ? (typeof src === 'string' ?
            (resource[src] || src) :
            (src[lang.replace('-', '_')] || src['default'])) : '';
    }, [resource, lang]);
    let date = React.useMemo(() => {
        let key = lang.replace('-', '');
        let sub = key.substring(0, 2);
        //@ts-ignore
        return key in locales ? locales[key] : (sub in locales ? locales[sub] : locales.enUS);
    }, [lang]);
    let setLanguage = React.useCallback((lang: string) => {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('lang', lang);
        }
        setLang(lang);
        try {
            setResource(require('../i18n/' + lang + '.json'));
        } catch {
            setResource({});
        }
        document.documentElement.lang = lang;
    }, []);
    React.useEffect(() => {
        lang && (document.documentElement.lang = lang);
    }, [lang]);
    React.useEffect(() => {
        if (props.init) {
            setLanguage(props.init);
        } else {
            let result = detectLanguage(localStorage.getItem('language') || navigator.languages);
            locale.lang = result[0];
            setLang(result[0]);
            locale.resource = result[1];
            setResource(result[1]);
        }
    }, [props.init]);
    return (
        <LocaleContext.Provider value={[__, lang, setLanguage, date]}>
            {props.children}
        </LocaleContext.Provider>
    );
}

export default function useLocale() {
    return React.useContext(LocaleContext);
}

export function I18N(props: { children: string | LocaleString }) {
    const {0: __, 1: lang} = useLocale();
    return (
        <span key={lang}>{__(props.children)}</span>
    );
}
